import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle, AlertTriangle, ShieldCheck } from 'lucide-react';

interface SystemProofCardProps {
  uptimePct: number;
  sentCount: number;
  resolvedCount: number;
  criticalCount: number;
  warningCount: number;
}

export function SystemProofCard({ uptimePct, sentCount, resolvedCount, criticalCount, warningCount }: SystemProofCardProps) {
  const issueCount = criticalCount + warningCount;
  const isHealthy = issueCount === 0 && uptimePct >= 95;

  return (
    <Card className="bg-wt-bg-secondary border-border">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
          {isHealthy ? (
            <ShieldCheck className="w-4 h-4 text-wt-green-primary" />
          ) : (
            <AlertTriangle className="w-4 h-4 text-wt-amber-primary" />
          )}
          System Health
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-baseline gap-2">
          <span className="font-mono text-2xl font-semibold text-foreground">{uptimePct.toFixed(1)}%</span>
          <span className="text-sm text-muted-foreground">uptime</span>
        </div>

        {isHealthy ? (
          <div className="flex items-center gap-2 text-wt-green-primary">
            <CheckCircle className="w-4 h-4" />
            <span className="text-sm">No issues detected this period</span>
          </div>
        ) : (
          <div className="space-y-2 text-sm">
            {criticalCount > 0 && (
              <div className="flex items-center gap-2 text-wt-red-primary">
                <span className="font-mono">{criticalCount}</span> critical alert{criticalCount !== 1 ? 's' : ''}
              </div>
            )}
            {warningCount > 0 && (
              <div className="flex items-center gap-2 text-wt-amber-primary">
                <span className="font-mono">{warningCount}</span> warning{warningCount !== 1 ? 's' : ''}
              </div>
            )}
            {resolvedCount > 0 && (
              <div className="flex items-center gap-2 text-wt-green-primary">
                <CheckCircle className="w-3 h-3" />
                <span className="font-mono">{resolvedCount}</span> resolved
              </div>
            )}
          </div>
        )}

        {sentCount > 0 && (
          <p className="text-xs text-muted-foreground">
            <span className="font-mono">{sentCount}</span> notification{sentCount !== 1 ? 's' : ''} sent this period
          </p>
        )}
      </CardContent>
    </Card>
  );
}
